import { useCallback, useEffect, useId, useState } from 'react';
import type { ChangeEvent, RefObject } from 'react';
import { useLogoUpload, type UploadedLogo, type LogoUploadHandlers } from '../../hooks';
import type { AccountThemeDto } from '../../api/accountThemeApi';

export interface ThemeLogoSectionViewModel {
  // Primary logo
  logo: UploadedLogo | null;
  logoError: string | null;
  isLogoDragActive: boolean;
  logoInputRef: RefObject<HTMLInputElement | null>;
  logoHandlers: LogoUploadHandlers;
  logoInputId: string;

  // Certificate logo
  certLogo: UploadedLogo | null;
  certLogoError: string | null;
  isCertLogoDragActive: boolean;
  certLogoInputRef: RefObject<HTMLInputElement | null>;
  certLogoHandlers: LogoUploadHandlers;
  certLogoInputId: string;

  // Certificate toggle
  useSeparateCertLogo: boolean;
  certificateSaveError: string | null;
  certToggleId: string;
  onToggleCertChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRevertCert: () => void;

  // Background colors for sample tiles
  logoBackgroundColor: string;
  certBackgroundColor: string;
}

export interface ThemeLogoSettingsInternals {
  primaryLogoAssetId: number | null;
  certificateLogoAssetId: number | null;
  primaryLogoFile: File | null;
  certificateLogoFile: File | null;
  setCertificateSaveError: (error: string | null) => void;
  seedLogosFromDto: (dto: AccountThemeDto | undefined) => void;
  sectionViewModel: ThemeLogoSectionViewModel;
}

export function useThemeLogoSettings(
  scopeAccountId: number | null | undefined,
  logoBackgroundColor: string,
  certBackgroundColor: string,
  disabled = false,
): ThemeLogoSettingsInternals {
  const logoInputId = useId();
  const certLogoInputId = useId();
  const certToggleId = useId();

  const {
    logo,
    setLogo: setLogoState,
    error: logoError,
    isDragActive: isLogoDragActive,
    inputRef: logoInputRef,
    handlers: logoHandlers,
    reset: resetLogo,
  } = useLogoUpload({ scopeAccountId, kind: 'logo', stageOnly: true, disabled });

  const {
    logo: certLogo,
    setLogo: setCertLogoState,
    error: certLogoError,
    isDragActive: isCertLogoDragActive,
    inputRef: certLogoInputRef,
    handlers: certLogoHandlers,
    reset: resetCertLogo,
  } = useLogoUpload({ scopeAccountId, kind: 'logo', stageOnly: true, disabled });

  // useSeparateCertLogo is seeded from the DTO (true if certificateLogoAssetId is set),
  // not derived from certLogo state, so it persists across reloads.
  const [useSeparateCertLogo, setUseSeparateCertLogo] = useState(false);
  const [certificateSaveError, setCertificateSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!useSeparateCertLogo || certLogo?.file) setCertificateSaveError(null);
  }, [useSeparateCertLogo, certLogo?.file]);

  const resetCertState = useCallback(() => {
    resetCertLogo();
    setUseSeparateCertLogo(false);
  }, [resetCertLogo]);

  const handleToggleCertChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (event.target.checked) {
        setUseSeparateCertLogo(true);
      } else {
        resetCertState();
      }
    },
    [resetCertState, disabled]
  );

  const handleRevertCert = useCallback(() => {
    if (disabled) return;
    resetCertState();
  }, [resetCertState, disabled]);

  // Resets and re-seeds both logo slots from the stored theme. `useSeparateCertLogo`
  // is derived from the DTO so it persists across reloads.
  const seedLogosFromDto = useCallback((dto: AccountThemeDto | undefined) => {
    resetLogo();
    if (dto?.primaryLogo) {
      const { id, url, filename } = dto.primaryLogo;
      setLogoState({ src: url, name: filename, assetId: id });
    }

    resetCertLogo();
    if (dto?.certificateLogo) {
      const { id, url, filename } = dto.certificateLogo;
      setCertLogoState({ src: url, name: filename, assetId: id });
    }

    setUseSeparateCertLogo(Boolean(dto?.certificateLogoAssetId));
    setCertificateSaveError(null);
  }, [resetLogo, resetCertLogo, setLogoState, setCertLogoState]);

  const primaryLogoAssetId = logo?.assetId ?? null;
  const certificateLogoAssetId = useSeparateCertLogo ? (certLogo?.assetId ?? null) : null;

  return {
    primaryLogoAssetId,
    certificateLogoAssetId,
    primaryLogoFile: logo?.file ?? null,
    certificateLogoFile: certLogo?.file ?? null,
    setCertificateSaveError,
    seedLogosFromDto,
    sectionViewModel: {
      logo,
      logoError,
      isLogoDragActive,
      logoInputRef,
      logoHandlers,
      logoInputId,
      certLogo,
      certLogoError,
      isCertLogoDragActive,
      certLogoInputRef,
      certLogoHandlers,
      certLogoInputId,
      useSeparateCertLogo,
      certificateSaveError,
      certToggleId,
      onToggleCertChange: handleToggleCertChange,
      onRevertCert: handleRevertCert,
      logoBackgroundColor,
      certBackgroundColor,
    },
  };
}
